import { useEffect, useState } from 'react';
import * as holidaysClient from './api/holidays-client';
import type { HolidayRow } from './api/holidays-client';
import { ApiError } from '@/lib/api-client';
import { showApiError } from '@/lib/error-toast';
import styles from './tenant-settings.module.css';

export function HolidaySection() {
  const [rows, setRows] = useState<HolidayRow[] | null>(null);
  const [date, setDate] = useState('');
  const [label, setLabel] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  async function loadAll() {
    setRows(await holidaysClient.list());
  }

  useEffect(() => {
    void loadAll();
  }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!date || !label.trim()) return;
    setAdding(true);
    setAddError(null);
    try {
      await holidaysClient.create({ date, label: label.trim(), recurringAnnual: recurring });
      setDate('');
      setLabel('');
      setRecurring(false);
      await loadAll();
    } catch (err) {
      // Duplicate-date and cap-reached both surface as a 409, but the API
      // returns a distinct `error` string for each — display it verbatim
      // (keep-with-invalid-state: date/label stay filled so the operator
      // can see and correct what they typed, matching EmailSection's
      // Task-12 chip-error convention).
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        setAddError(body?.error ?? 'That date is already on the calendar');
      } else {
        showApiError(err, { fallback: 'Failed to add holiday' });
      }
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(id: string) {
    try {
      await holidaysClient.remove(id);
      await loadAll();
    } catch (err) {
      showApiError(err, { fallback: 'Failed to remove holiday' });
    }
  }

  // The parent <section className={styles.section}><h2>Holidays</h2> already
  // labels this surface (matches EmailSection's convention — no doubled
  // heading IA noise).
  if (!rows) {
    return <p>Loading…</p>;
  }

  return (
    <div className={styles.backupForm}>
      <form onSubmit={handleAdd} className={styles.actionRow}>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={styles.textInput}
          aria-label="Holiday date"
        />
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (e.g. Christmas Day)"
          className={styles.textInput}
          aria-label="Holiday label"
        />
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={recurring}
            onChange={(e) => setRecurring(e.target.checked)}
          />
          Repeats every year
        </label>
        <button type="submit" disabled={adding || !date || !label.trim()} className={styles.primaryButton}>
          {adding ? 'Adding…' : 'Add'}
        </button>
      </form>
      {addError && <p className={styles.chipError}>{addError}</p>}

      {rows.length === 0 ? (
        <p className={styles.emptyState}>No holidays configured — NETWORKDAYS/WORKDAY treat weekends only.</p>
      ) : (
        <table className={styles.pendingTable}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Label</th>
              <th>Repeats</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.recurringAnnual ? r.date.slice(5) : r.date}</td>
                <td>{r.label}</td>
                <td>{r.recurringAnnual ? 'Yearly' : '—'}</td>
                <td>
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => handleRemove(r.id)}
                    aria-label={`Remove ${r.label}`}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
